import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getReferralCode } from '../utils/referral';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Calendar, Clock, MapPin, Users, Video, ArrowRight, Repeat, CheckCircle2, Copy, Ticket } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeColors = {
  webinar: 'bg-[#EFF6FF] text-[#1D4ED8]',
  workshop: 'bg-[#FFF7ED] text-[#F97316]',
  networking: 'bg-green-100 text-green-700',
  conference: 'bg-purple-100 text-purple-700',
  training: 'bg-[#EFF6FF] text-[#1D4ED8]',
};

const blankForm = { first_name: '', last_name: '', email: '', phone: '', selected_times: [] };

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [paidEvents, setPaidEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);
  const [regForm, setRegForm] = useState(blankForm);
  const [confirmation, setConfirmation] = useState(null); // { eventId, event, zoom_join_url, zoom_password }
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isPaidPrice = (price) => {
    if (!price) return false;
    if (typeof price === 'string' && price.toLowerCase().includes('free')) return false;
    const cleaned = String(price).replace(/[^0-9.]/g, '');
    return cleaned && parseFloat(cleaned) > 0;
  };

  useEffect(() => {
    const ac = new AbortController();
    axios
      .get(`${API_URL}/api/events`, { signal: ac.signal })
      .then((res) => setEvents(res.data))
      .catch((err) => {
        if (axios.isCancel(err) || err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
        toast.error('Failed to load events');
      })
      .finally(() => setLoading(false));
    // Ticketed events (Events + Tickets are unified on this page)
    axios.get(`${API_URL}/api/paid-events`, { signal: ac.signal })
      .then((res) => setPaidEvents(res.data || []))
      .catch(() => {});
    return () => ac.abort();
  }, []);

  const handleRegister = async (e, event) => {
    e.preventDefault();
    const eventId = event.event_id;
    const paid = isPaidPrice(event.price);

    if (!paid) {
      // For events with time_slots, require at least one selection
      if (Array.isArray(event.time_slots) && event.time_slots.length > 1 && (!regForm.selected_times || regForm.selected_times.length === 0)) {
        toast.error('Pick at least one session time');
        return;
      }
      setSubmitting(true);
      try {
        const res = await axios.post(`${API_URL}/api/events/${eventId}/register`, {
          first_name: regForm.first_name,
          last_name: regForm.last_name,
          email: regForm.email,
          phone: regForm.phone,
          selected_times: regForm.selected_times && regForm.selected_times.length ? regForm.selected_times : null,
        });
        toast.success('Successfully registered!');
        setConfirmation({
          eventId,
          event,
          zoom_join_url: res.data.zoom_join_url,
          zoom_password: res.data.zoom_password,
          is_recurring: res.data.is_recurring,
          recurrence_label: res.data.recurrence_label,
          selected_times: res.data.selected_times || [],
          email: regForm.email,
        });
        setRegistering(null);
        setRegForm(blankForm);
        const refresh = await axios.get(`${API_URL}/api/events`);
        setEvents(refresh.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to register');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setProcessingPayment(true);
    const payload = {
      item_type: 'event',
      item_id: eventId,
      origin_url: window.location.origin,
      name: `${regForm.first_name} ${regForm.last_name}`.trim(),
      email: regForm.email,
      ref: getReferralCode(),
    };
    try {
      const endpoint = paymentProvider === 'paypal'
        ? `${API_URL}/api/payments/paypal/create-order`
        : `${API_URL}/api/payments/checkout`;
      const res = await axios.post(endpoint, payload);
      if (!res.data.url) throw new Error('No checkout URL returned');
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to start ${paymentProvider} checkout`);
      setProcessingPayment(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateStr; }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Layout title="Events - Live Trainings, Webinars & Q&A" description="Join live trainings, webinars, Q&A sessions, and networking events designed to help entrepreneurs learn, connect, and grow with structure.">
      <div className="bg-[#FAFAFA]" data-testid="events-page">
        {/* Hero */}
        <section className="bg-[#0F172A] text-white py-12">
          <div className="container-custom text-center">
            <span className="inline-block px-4 py-1 bg-[#F97316]/20 text-[#FB923C] text-sm font-bold rounded-full mb-3">
              Community & Ecosystem
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="events-title">
              Live Trainings, Webinars & Q&A
            </h1>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              Join live trainings, webinars, Q&A sessions, and networking events designed to help entrepreneurs learn, connect, and grow with structure.
            </p>
          </div>
        </section>

        {/* Ticketed Events & Workshops (merged from the old Tickets page) */}
        {paidEvents.length > 0 && (
          <section className="container-custom pt-10" data-testid="ticketed-events-section">
            <div className="flex items-center gap-2 mb-1">
              <Ticket size={18} className="text-[#F97316]" />
              <h2 className="text-2xl font-bold text-[#0A0F1C]">Ticketed Events & Workshops</h2>
            </div>
            <p className="text-sm text-[#475569] mb-5">Reserve your seat for summits, workshops, and live intensives.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paidEvents.map((ev) => {
                const from = ev.ticket_tiers && ev.ticket_tiers.length ? Math.min(...ev.ticket_tiers.map(t => t.price)) : 0;
                return (
                  <Link to={`/tickets/${ev.slug}`} key={ev.event_id} className="block bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`ticketed-event-${ev.slug}`}>
                    {ev.image_url ? <img src={ev.image_url} alt={ev.title} className="w-full h-40 object-cover" /> : <div className="w-full h-40 bg-gradient-to-br from-[#0F172A] to-[#F97316] flex items-center justify-center"><Ticket size={40} className="text-white/40" /></div>}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-[#0A0F1C] mb-2">{ev.title}</h3>
                      <div className="space-y-1 text-sm text-[#475569]">
                        {ev.starts_at && <div className="flex items-center gap-2"><Calendar size={14} className="text-[#F97316]" />{new Date(ev.starts_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</div>}
                        {(ev.venue || ev.location) && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#F97316]" />{[ev.venue, ev.location].filter(Boolean).join(', ')}</div>}
                      </div>
                      <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#F97316]">From ${from} <ArrowRight size={14} /></div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Events List */}
        <section className="container-custom py-10">
          {paidEvents.length > 0 && (
            <h2 className="text-2xl font-bold text-[#0A0F1C] mb-5 flex items-center gap-2"><Repeat size={18} className="text-[#1D4ED8]" />Live Trainings & Webinars</h2>
          )}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <Calendar size={48} className="text-[#E2E8F0] mx-auto mb-4" />
              <p className="text-[#475569]">No upcoming events. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {events.map((event, index) => (
                <div
                  key={event.event_id}
                  className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`event-card-${index}`}
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Date badge */}
                    <div className="lg:w-48 bg-[#0F172A] text-white p-6 flex flex-col items-center justify-center">
                      <span className="text-sm text-[#94A3B8] uppercase">
                        {new Date(event.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="text-4xl font-bold">
                        {new Date(event.event_date + 'T00:00:00').getDate()}
                      </span>
                      <span className="text-sm text-[#94A3B8]">
                        {new Date(event.event_date + 'T00:00:00').getFullYear()}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-grow p-6">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${typeColors[event.event_type] || 'bg-[#F1F5F9] text-[#475569]'}`}>
                          {event.event_type}
                        </span>
                        {event.is_virtual && (
                          <span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-600 flex items-center gap-1">
                            <Video size={12} /> Virtual
                          </span>
                        )}
                        {event.is_recurring && (
                          <span className="text-xs px-3 py-1 rounded-full bg-[#FFF7ED] text-[#9A3412] flex items-center gap-1" data-testid={`recurring-badge-${index}`}>
                            <Repeat size={12} /> Recurring
                          </span>
                        )}
                        {event.price && (
                          <span className="text-sm font-bold text-[#0A0F1C]">{event.price}</span>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-[#0A0F1C] mb-2">{event.title}</h3>
                      <p className="text-sm text-[#475569] mb-4">{event.short_description}</p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#475569]">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} className="text-[#1D4ED8]" />
                          {formatDate(event.event_date)}
                        </span>
                        {event.event_time && (
                          <span className="flex items-center gap-1">
                            <Clock size={14} className="text-[#1D4ED8]" />
                            {event.event_time}
                          </span>
                        )}
                        {event.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} className="text-[#1D4ED8]" />
                            {event.location}
                          </span>
                        )}
                        {event.capacity && (
                          <span className="flex items-center gap-1">
                            <Users size={14} className="text-[#1D4ED8]" />
                            {event.registered_count || 0}/{event.capacity} spots
                          </span>
                        )}
                      </div>
                      {event.is_recurring && event.recurrence_label && (
                        <p className="text-xs text-[#9A3412] mt-3 font-medium">{event.recurrence_label}</p>
                      )}
                    </div>

                    {/* Action */}
                    <div className="lg:w-72 p-6 flex items-center justify-center border-t lg:border-t-0 lg:border-l border-[#E2E8F0]">
                      {confirmation && confirmation.eventId === event.event_id ? (
                        <ConfirmationPanel
                          info={confirmation}
                          onCopy={copyToClipboard}
                          onDismiss={() => setConfirmation(null)}
                          testIdSuffix={index}
                        />
                      ) : registering === event.event_id ? (
                        <form onSubmit={(e) => handleRegister(e, event)} className="w-full space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="First name"
                              value={regForm.first_name}
                              onChange={(e) => setRegForm({ ...regForm, first_name: e.target.value })}
                              required
                              data-testid={`reg-first-name-${index}`}
                            />
                            <Input
                              placeholder="Last name"
                              value={regForm.last_name}
                              onChange={(e) => setRegForm({ ...regForm, last_name: e.target.value })}
                              required
                              data-testid={`reg-last-name-${index}`}
                            />
                          </div>
                          <Input
                            type="email"
                            placeholder="Email"
                            value={regForm.email}
                            onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                            required
                            data-testid={`reg-email-${index}`}
                          />
                          <Input
                            type="tel"
                            placeholder="Phone (optional)"
                            value={regForm.phone}
                            onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                            data-testid={`reg-phone-${index}`}
                          />
                          {/* Time-slot picker (recurring events with multiple times) */}
                          {Array.isArray(event.time_slots) && event.time_slots.length > 1 && (
                            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-2" data-testid={`time-picker-${index}`}>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1.5">
                                Which time will you attend?
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                {event.time_slots.map((slot) => {
                                  const checked = (regForm.selected_times || []).includes(slot);
                                  return (
                                    <label
                                      key={slot}
                                      className={`text-[11px] cursor-pointer flex items-center justify-center gap-1 py-1.5 px-2 rounded border transition-all ${
                                        checked
                                          ? 'bg-[#1D4ED8] text-white border-[#1D4ED8] font-bold'
                                          : 'bg-white text-[#475569] border-[#E2E8F0]'
                                      }`}
                                      data-testid={`time-slot-${index}-${slot.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          const cur = regForm.selected_times || [];
                                          setRegForm({
                                            ...regForm,
                                            selected_times: checked
                                              ? cur.filter((x) => x !== slot)
                                              : [...cur, slot],
                                          });
                                        }}
                                        className="hidden"
                                      />
                                      {slot}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {isPaidPrice(event.price) && (
                            <div data-testid={`event-payment-provider-${index}`}>
                              <div className="grid grid-cols-2 gap-1.5 mt-1">
                                <button
                                  type="button"
                                  onClick={() => setPaymentProvider('stripe')}
                                  className={`text-[10px] font-semibold py-1.5 px-2 rounded border transition-all ${
                                    paymentProvider === 'stripe'
                                      ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]'
                                      : 'bg-white text-[#475569] border-[#E2E8F0]'
                                  }`}
                                  data-testid={`event-pay-stripe-${index}`}
                                >
                                  Stripe
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPaymentProvider('paypal')}
                                  className={`text-[10px] font-semibold py-1.5 px-2 rounded border transition-all ${
                                    paymentProvider === 'paypal'
                                      ? 'bg-[#003087] text-white border-[#003087]'
                                      : 'bg-white text-[#475569] border-[#E2E8F0]'
                                  }`}
                                  data-testid={`event-pay-paypal-${index}`}
                                >
                                  PayPal
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button type="button" onClick={() => { setRegistering(null); setRegForm(blankForm); }} className="btn-outline flex-1 text-xs" disabled={processingPayment || submitting}>
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              className="btn-primary-orange flex-1 text-xs"
                              disabled={processingPayment || submitting}
                              data-testid={`register-submit-${index}`}
                            >
                              {submitting || processingPayment ? '...' : isPaidPrice(event.price) ? `Pay ${event.price}` : 'Register'}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <Button
                          onClick={() => { setRegistering(event.event_id); setConfirmation(null); }}
                          className="btn-primary-blue w-full flex items-center justify-center gap-2"
                          data-testid={`register-btn-${index}`}
                        >
                          Register <ArrowRight size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function ConfirmationPanel({ info, onCopy, onDismiss, testIdSuffix }) {
  return (
    <div className="w-full space-y-3 text-center" data-testid={`registration-confirmation-${testIdSuffix}`}>
      <div className="flex justify-center">
        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
          <CheckCircle2 size={28} className="text-green-600" />
        </div>
      </div>
      <div>
        <div className="font-bold text-[#0A0F1C] text-base">You're registered!</div>
        <div className="text-xs text-[#475569] mt-1">Confirmation sent to {info.email}</div>
      </div>
      {info.zoom_join_url ? (
        <div className="text-left bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#475569] mb-1">Zoom Link</div>
          <a
            href={info.zoom_join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#1D4ED8] break-all hover:underline"
            data-testid={`confirmation-zoom-link-${testIdSuffix}`}
          >
            {info.zoom_join_url}
          </a>
          {info.zoom_password && (
            <div className="text-xs text-[#475569] mt-1">
              Passcode: <strong>{info.zoom_password}</strong>
            </div>
          )}
          <button
            onClick={() => onCopy(info.zoom_join_url)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#1D4ED8] font-semibold hover:underline"
            data-testid={`confirmation-copy-zoom-${testIdSuffix}`}
          >
            <Copy size={11} /> Copy link
          </button>
        </div>
      ) : (
        <div className="text-xs text-[#475569] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          David will email you the meeting link before the session.
        </div>
      )}
      {info.selected_times && info.selected_times.length > 0 && (
        <div className="bg-[#EFF6FF] border border-[#DBEAFE] rounded-lg p-2 text-[11px] text-[#1E40AF] font-semibold" data-testid={`confirmation-times-${testIdSuffix}`}>
          You picked: {info.selected_times.join(' and ')}
        </div>
      )}
      {info.is_recurring && info.recurrence_label && (
        <div className="text-[11px] text-[#9A3412] bg-[#FFF7ED] border border-[#FFEDD5] rounded px-2 py-1">
          {info.recurrence_label}. Same link every week, attend either time
        </div>
      )}
      <Link
        to="/schedule"
        className="block w-full bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold py-2.5 px-3 rounded-lg transition-colors"
        data-testid={`confirmation-strategy-cta-${testIdSuffix}`}
      >
        Want a 1:1? Book a Strategy Session →
      </Link>
      <button
        onClick={onDismiss}
        className="text-[11px] text-[#94A3B8] hover:text-[#475569]"
        data-testid={`confirmation-dismiss-${testIdSuffix}`}
      >
        Close
      </button>
    </div>
  );
}
